document.addEventListener('DOMContentLoaded', () => {
  // --- 1. Type Definitions ---

  /**
   * Defines the structure for a single brand's comparison data.
   * @typedef {object} BrandData
   * @property {string} brand - The name of the brand.
   * @property {string} cooling_power - Cooling power spec (e.g., "1.0 HP").
   * @property {string} cools_to - Minimum temperature (e.g., "32°F").
   * @property {boolean} makes_ice - Feature flag.
   * @property {boolean} heater_included - Feature flag.
   * @property {boolean} ozone_sanitation - Feature flag.
   * @property {boolean} free_next_business_day_shipping - Feature flag.
   * @property {boolean} smartphone_controlled - Feature flag.
   * @property {boolean} insulated_marine_grade_cover - Feature flag.
   * @property {boolean} one_year_free_filters - Feature flag.
   */

  /**
   * A map of competitor data, keyed by the brand's slug.
   * @typedef {Object.<string, BrandData>} CompetitorMap
   */

  /**
   * Defines the specific keys from BrandData that are used in the feature map.
   * @typedef {('cooling_power' | 'cools_to' | 'makes_ice' | 'heater_included' | 'ozone_sanitation' | 'free_next_business_day_shipping' | 'smartphone_controlled' | 'insulated_marine_grade_cover' | 'one_year_free_filters')} FeatureKey
   */

  // --- 2. Helper Functions ---

  /**
   * @param {string} brandName
   * @returns {string}
   */
  function slugify(brandName) {
    return brandName.toLowerCase().replace(/\s+/g, '-');
  }

  /**
   * @param {string | boolean} value
   * @param {number} cellIndex
   * @returns {string}
   */
  function formatValue(value, cellIndex) {
    if (typeof value === 'boolean') {
      if (value && cellIndex === 1) {
        return '<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="20" fill="url(#g1)" fill-opacity="0.4"/><circle cx="20" cy="20" r="19.69" stroke="url(#g2)" stroke-opacity="0.1" stroke-width="0.63"/><polyline points="13.77 20.5 17.75 24.48 26.75 15.48" stroke="white" stroke-linecap="round"/><defs><linearGradient id="g1" x1="20" y1="0" x2="20" y2="40" gradientUnits="userSpaceOnUse"><stop stop-color="white" stop-opacity="0.6"/><stop offset="1" stop-color="#E6E6E6" stop-opacity="0.05"/></linearGradient><linearGradient id="g2" x1="20" y1="0" x2="20" y2="40" gradientUnits="userSpaceOnUse"><stop stop-color="white" stop-opacity="0.2"/><stop offset="1" stop-color="#F2F2F2" stop-opacity="0.8"/></linearGradient></defs></svg>';
      } else if (value && cellIndex === 2) {
        return '<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.3222 25.9619L6.86719 18.5069L7.93819 17.4374L14.3222 23.8214L28.0562 10.0874L29.1257 11.1584L14.3222 25.9619Z" fill="currentColor"/></svg>';
      } else {
        return '<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.9991 19.0621L10.1301 26.9311C9.99013 27.0711 9.81812 27.1461 9.61412 27.1561C9.41012 27.1661 9.22812 27.0911 9.06812 26.9311C8.90812 26.7711 8.82813 26.5941 8.82812 26.4001C8.82812 26.2061 8.90812 26.0291 9.06812 25.8691L16.9371 18.0001L9.06812 10.1311C8.92812 9.9911 8.85312 9.8191 8.84312 9.6151C8.83312 9.4111 8.90812 9.2291 9.06812 9.0691C9.22812 8.9091 9.40512 8.8291 9.59912 8.8291C9.79312 8.8291 9.97013 8.9091 10.1301 9.0691L17.9991 16.9381L25.8681 9.0691C26.0081 8.9291 26.1806 8.8541 26.3856 8.8441C26.5886 8.8341 26.7701 8.9091 26.9301 9.0691C27.0901 9.2291 27.1701 9.4061 27.1701 9.6001C27.1701 9.7941 27.0901 9.9711 26.9301 10.1311L19.0611 18.0001L26.9301 25.8691C27.0701 26.0091 27.1451 26.1816 27.1551 26.3866C27.1651 26.5896 27.0901 26.7711 26.9301 26.9311C26.7701 27.0911 26.5931 27.1711 26.3991 27.1711C26.2051 27.1711 26.0281 27.0911 25.8681 26.9311L17.9991 19.0621Z" fill="currentColor"/></svg>';
      }
    }
    return String(value);
  }

  /**
   * Fills a column's cells with data.
   * @param {BrandData | undefined} data - The brand's data object.
   * @param {NodeListOf<HTMLElement>} cells - The collection of <div> cells for this column.
   * @param {FeatureKey[]} featureMap - The array of property keys.
   * @param {number} cellIndex - The index of the column (1 or 2).
   * @returns {void}
   */
  function populateColumn(data, cells, featureMap, cellIndex) {
    if (!data) return;

    featureMap.forEach((key, index) => {
      const cell = cells[index];
      if (cell) {
        // --- THIS IS THE CHANGE ---
        // Find the .comparison-grid__cell-value span inside the cell
        const targetSpan = /** @type {HTMLElement | null} */ (cell.querySelector('.comparison-grid__cell-value'));

        if (targetSpan) {
          targetSpan.innerHTML = formatValue(data[key], cellIndex);
        }
        // --- END CHANGE ---
      }
    });
  }

  // --- 3. Feature Map Definition ---

  /** @type {FeatureKey[]} */
  const featureMap = [
    'cooling_power',
    'cools_to',
    'makes_ice',
    'heater_included',
    'ozone_sanitation',
    'free_next_business_day_shipping',
    'smartphone_controlled',
    'insulated_marine_grade_cover',
    'one_year_free_filters'
  ];

  // --- 4. Main Execution ---

  /**
   * Finds, parses, and sets up a single comparison table.
   * @param {HTMLScriptElement} jsonDataElement The <script> tag with the data.
   */
  function setupComparisonTable(jsonDataElement) {
    const tableId = jsonDataElement.dataset.target;
    if (!tableId) {
      console.warn('Comparison table script: Found script tag with no data-target.');
      return;
    }

    const tableElement = /** @type {HTMLElement | null} */ (document.getElementById(tableId));
    if (!tableElement) {
      console.warn(`Comparison table script: Could not find grid with ID: ${tableId}`);
      return;
    }

    // --- Selectors remain the same ---
    const selectElement = /** @type {HTMLSelectElement | null} */ (tableElement.querySelector('select.hidden'));
    const triggerLabel = /** @type {HTMLElement | null} */ (tableElement.querySelector('.custom-select__trigger .custom-select__option-label'));

    const col1Cells = tableElement.querySelectorAll('[data-js-target-col="1"]');
    const col2Cells = tableElement.querySelectorAll('[data-js-target-col="2"]');

    if (!selectElement || !triggerLabel || col1Cells.length === 0 || col2Cells.length === 0) {
      console.warn(`Comparison table script: Missing critical elements (select, triggerLabel, or data cells) in grid: ${tableId}`);
      return;
    }

    if (!jsonDataElement.textContent) {
      console.error(`Comparison table script: No JSON content found in script for ${tableId}`);
      return;
    }

    /** @type {BrandData[]} */
    let allBrandsData;
    try {
      allBrandsData = JSON.parse(jsonDataElement.textContent).brands;
    } catch (e) {
      console.error(`Comparison table script: Invalid JSON in ${tableId}`, e);
      return;
    }

    if (!Array.isArray(allBrandsData)) {
      console.error(`Comparison table script: Parsed JSON for ${tableId} is not in the expected format.`);
      return;
    }

    const polarMonkeysData = allBrandsData.find(brand => brand.brand === "Polar Monkeys");

    /** @type {CompetitorMap} */
    const competitorMap = allBrandsData.reduce((/** @type {CompetitorMap} */ accumulator, brand) => {
      if (brand.brand !== "Polar Monkeys") {
        accumulator[slugify(brand.brand)] = brand;
      }
      return accumulator;
    }, {});

    // --- 5. Initial Population ---

    if (polarMonkeysData) {
      populateColumn(polarMonkeysData, col1Cells, featureMap, 1);
    } else {
      console.warn(`Comparison table script: "Polar Monkeys" data not found for table ${tableId}`);
    }

    populateColumn(competitorMap[selectElement.value], col2Cells, featureMap, 2);

    // --- 6. Event Listener ---

    selectElement.addEventListener('change', (event) => {
      const target = /** @type {HTMLSelectElement} */ (event.target);
      const brandKey = target.value;

      if (!brandKey) return;

      populateColumn(competitorMap[brandKey], col2Cells, featureMap, 2);
    });
  }

  // Find all JSON data elements
  /** @type {NodeListOf<HTMLScriptElement>} */
  const allJsonDataElements = document.querySelectorAll('script[data-comparison-grid-json]');

  // Set up each table
  allJsonDataElements.forEach(setupComparisonTable);
});